import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MouseEvent, type ReactNode, type RefObject } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMobileNavGsap, type MobileNavPanel } from '../../hooks/useMobileNavGsap.js';

export type { MobileNavPanel };
import { Icon } from '@/components/icon/index.js';
import type { IconName } from '@/components/icon/index.js';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { cn } from '../../lib/utils.js';
import { Button } from '../design-system/primitives/Button/Button.js';
import { resolveAvatarSrc } from '../../lib/avatar.js';
import { SUPPORTED_LANGUAGES, setLanguage, type SupportedLanguage } from '../../i18n.js';
import { useAuthStore, type AuthPack, type AuthUser } from '../../store/authStore.js';
import { useLandingSectionNav } from '../../hooks/useLandingSectionNav.js';
import { landingPathToSectionId } from '../../lib/landingScroll.js';
import { MOBILE_NAV_TOGGLE_CLASS, MobileNavHeaderShell } from './mobileNavChrome.js';

type MobileNavMenuProps = {
  open: boolean;
  onClose: () => void;
  variant: 'landing' | 'app';
  headerRef: RefObject<HTMLElement | null>;
  landingScrolled?: boolean;
  hideGuestAuthButtons?: boolean;
  showGuestLanguageOnly?: boolean;
  /** Minimal chrome for gated states: only Language + Sign Out for signed-in users. */
  restricted?: boolean;
};

const adminNavItems = [
  { to: '/admin', labelKey: 'admin.sidebar.overview', defaultLabel: 'Overview', iconName: 'home' as const },
  { to: '/admin/users', labelKey: 'admin.sidebar.users', defaultLabel: 'Users', iconName: 'users' as const },
  { to: '/admin/simulations', labelKey: 'admin.sidebar.simulations', defaultLabel: 'Simulations', iconName: 'file-text' as const },
  { to: '/admin/payments', labelKey: 'admin.sidebar.payments', defaultLabel: 'Payments', iconName: 'credit-card' as const },
  { to: '/admin/analytics', labelKey: 'admin.sidebar.analytics', defaultLabel: 'Analytics', iconName: 'bar-chart-3' as const },
  { to: '/admin/logs', labelKey: 'admin.sidebar.logs', defaultLabel: 'Logs', iconName: 'scroll-text' as const },
  { to: '/admin/prompts', labelKey: 'admin.sidebar.prompts', defaultLabel: 'Prompts', iconName: 'code-2' as const },
  { to: '/admin/test-lab', labelKey: 'admin.sidebar.testLab', defaultLabel: 'Test Lab', iconName: 'flask-conical' as const },
  {
    to: '/admin/interview-settings',
    labelKey: 'admin.sidebar.interviewSettings',
    defaultLabel: 'Interview Defaults',
    iconName: 'settings-2' as const,
  },
  { to: '/admin/promo-codes', labelKey: 'admin.sidebar.promoCodes', defaultLabel: 'Promo Codes', iconName: 'gift' as const },
  { to: '/admin/newsletters', labelKey: 'admin.sidebar.newsletters', defaultLabel: 'Newsletters', iconName: 'mail' as const },
  {
    to: '/admin/newsletters/subscribers',
    labelKey: 'admin.sidebar.newsletterSubscribers',
    defaultLabel: 'Subscribers',
    iconName: 'user-round-cog' as const,
  },
  { to: '/admin/support', labelKey: 'admin.sidebar.support', defaultLabel: 'Support', iconName: 'message-circle' as const },
  { to: '/admin/email-test', labelKey: 'admin.sidebar.emailTest', defaultLabel: 'Email Test', iconName: 'mail-plus' as const },
] as const;

const settingsNavItems = [
  { to: '/settings/profile', labelKey: 'settings.tabs.profile', defaultLabel: 'Profile', iconName: 'user-2' as const },
  { to: '/settings/history', labelKey: 'settings.tabs.history', defaultLabel: 'History', iconName: 'history' as const },
  { to: '/settings/plan', labelKey: 'settings.tabs.pack', defaultLabel: 'My Plan', iconName: 'credit-card' as const },
  { to: '/settings/billing', labelKey: 'settings.tabs.billing', defaultLabel: 'Billing', iconName: 'file-text' as const },
  {
    to: '/settings/promo-codes',
    labelKey: 'settings.tabs.promoCodes',
    defaultLabel: 'Promo Codes',
    iconName: 'gift' as const,
  },
  { to: '/settings/security', labelKey: 'settings.tabs.security', defaultLabel: 'Security', iconName: 'shield' as const },
  { to: '/settings/privacy', labelKey: 'settings.tabs.privacy', defaultLabel: 'Privacy', iconName: 'scroll-text' as const },
  {
    to: '/settings/notifications',
    labelKey: 'settings.tabs.notifications',
    defaultLabel: 'Notifications',
    iconName: 'mail' as const,
  },
] as const;

function isAdminNavItemActive(pathname: string, to: string): boolean {
  if (to === '/admin') return pathname === '/admin';
  if (to === '/admin/newsletters') {
    return (
      (pathname.startsWith('/admin/newsletters') && !pathname.startsWith('/admin/newsletters/subscribers')) ||
      pathname === '/admin/newsletter/compose'
    );
  }
  if (to === '/admin/newsletters/subscribers') return pathname.startsWith('/admin/newsletters/subscribers');
  return pathname.startsWith(to);
}

function isSettingsNavActive(pathname: string, to: string): boolean {
  if (to === '/settings/profile') {
    return pathname === '/settings' || pathname === '/settings/profile';
  }
  return pathname === to || pathname.startsWith(`${to}/`);
}

export function MobileNavMenu({
  open,
  onClose,
  variant,
  headerRef,
  landingScrolled = false,
  hideGuestAuthButtons = false,
  showGuestLanguageOnly = false,
  restricted = false,
}: MobileNavMenuProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useAuthStore((s) => s.user);
  const pack = useAuthStore((s) => s.pack);
  const refreshProfile = useAuthStore((s) => s.refreshProfile);
  const signOut = useAuthStore((s) => s.signOut);
  const setDisplayLanguage = useAuthStore((s) => s.setDisplayLanguage);
  const [panel, setPanel] = useState<MobileNavPanel>('main');
  const [mounted, setMounted] = useState(false);
  const shellRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const currentLang = (i18n.language?.slice(0, 2) as SupportedLanguage) ?? 'en';
  const languageLabels: Record<SupportedLanguage, string> = {
    en: t('common.english'),
    pl: t('common.polish'),
  };

  const handleCloseComplete = useCallback(() => {
    setMounted(false);
    setPanel('main');
  }, []);

  const { navigatePanel } = useMobileNavGsap({
    open,
    mounted,
    panel,
    variant,
    landingScrolled,
    currentLang,
    setPanel,
    shellRef,
    contentRef,
    headerRef,
    onCloseComplete: handleCloseComplete,
  });

  useLayoutEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  useEffect(() => {
    if (open && user) void refreshProfile();
  }, [open, user, refreshProfile]);

  const initials =
    user?.name?.trim()?.[0]?.toUpperCase() ??
    user?.email?.trim()?.[0]?.toUpperCase() ??
    '?';

  const landingNavLinks = [
    { to: '/how-it-works', label: t('landing.nav.howItWorks'), iconName: 'compass' as const },
    { to: '/features', label: t('landing.nav.features'), iconName: 'sparkles' as const },
    { to: '/pricing', label: t('landing.nav.pricing'), iconName: 'tag' as const },
    { to: '/faq', label: t('landing.nav.faq'), iconName: 'message-circle' as const },
  ];
  const { navigateToLandingSection } = useLandingSectionNav();

  const handleMobileLandingNavClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, path: string) => {
      if (variant !== 'landing') return;
      const sectionId = landingPathToSectionId(path);
      if (!sectionId) return;
      event.preventDefault();
      onClose();
      window.setTimeout(() => {
        navigateToLandingSection(path);
      }, 100);
    },
    [variant, onClose, navigateToLandingSection],
  );

  const packRemaining = pack ? Math.max(0, pack.totalSessions - pack.usedSessions) : 0;
  const packProgress =
    pack && pack.totalSessions > 0 ? Math.min(100, (pack.usedSessions / pack.totalSessions) * 100) : 0;
  const packTypeLabel = pack?.type
    ? pack.type.charAt(0).toUpperCase() + pack.type.slice(1).toLowerCase()
    : 'Pro';

  const onPickLanguage = (code: SupportedLanguage) => {
    if (user) {
      void setDisplayLanguage(code);
    } else {
      setLanguage(code);
    }
  };

  if (!mounted) return null;

  return (
    <div
      ref={shellRef}
      className={cn(
        'fixed inset-x-0 bottom-0 z-[60] flex flex-col overflow-hidden bg-ig-bg-primary will-change-transform',
        variant === 'landing' ? 'top-[60px]' : 'top-16',
      )}
      aria-hidden={!open}
    >
      {panel !== 'main' ? (
        <div>
          <MobileMenuSubHeader
            variant={variant}
            title={
              panel === 'language'
                ? t('nav.language')
                : panel === 'settings'
                  ? t('nav.settings')
                  : t('admin.breadcrumbs.admin', { defaultValue: 'Admin Panel' })
            }
            showAdminBadge={panel === 'admin'}
            onBack={() => navigatePanel('main', 'back')}
          />
        </div>
      ) : null}

      <div
        ref={contentRef}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 pb-8 pt-6"
      >
        {panel === 'main' && user ? (
          <LoggedInMainPanel
            user={user}
            initials={initials}
            pack={pack}
            packRemaining={packRemaining}
            packProgress={packProgress}
            packTypeLabel={packTypeLabel}
            currentLangLabel={languageLabels[currentLang]}
            isAdmin={user.role === 'ADMIN'}
            restricted={restricted}
            onNavigate={onClose}
            onOpenLanguage={() => navigatePanel('language', 'forward')}
            onOpenSettings={() => navigatePanel('settings', 'forward')}
            onOpenAdmin={() => navigatePanel('admin', 'forward')}
            onSignOut={() => {
              onClose();
              void signOut().then(() => navigate('/'));
            }}
            t={t}
          />
        ) : null}

        {panel === 'main' && !user ? (
          <GuestMainPanel
            variant={variant}
            landingNavLinks={landingNavLinks}
            currentLangLabel={languageLabels[currentLang]}
            onClose={onClose}
            onLandingSectionClick={handleMobileLandingNavClick}
            onOpenLanguage={() => navigatePanel('language', 'forward')}
            hideGuestAuthButtons={hideGuestAuthButtons}
            showGuestLanguageOnly={showGuestLanguageOnly}
            t={t}
          />
        ) : null}

        {panel === 'language' ? (
          <LanguagePanel
            currentLang={currentLang}
            languageLabels={languageLabels}
            showFooter={!user}
            onPick={onPickLanguage}
          />
        ) : null}

        {panel === 'settings' ? (
          <SettingsPanel pathname={pathname} packRemaining={packRemaining} onNavigate={onClose} t={t} />
        ) : null}

        {panel === 'admin' ? (
          <AdminPanel pathname={pathname} onNavigate={onClose} onBackToApp={onClose} t={t} />
        ) : null}
      </div>
    </div>
  );
}

function MobileMenuSubHeader({
  variant,
  title,
  showAdminBadge,
  onBack,
}: {
  variant: MobileNavMenuProps['variant'];
  title: string;
  showAdminBadge?: boolean;
  onBack: () => void;
}) {
  const { t } = useTranslation();

  return (
    <MobileNavHeaderShell variant={variant}>
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className={`${MOBILE_NAV_TOGGLE_CLASS} border-ig-border bg-transparent p-0 hover:bg-transparent`}
          aria-label={t('common.back', { defaultValue: 'Back' })}
          onClick={onBack}
        >
          <Icon name="chevron-left" className="h-5 w-5" />
        </Button>
        <span className="truncate font-sans text-[18px] font-normal leading-tight tracking-tight text-ig-text-primary">
          {title}
        </span>
        {showAdminBadge ? (
          <span className="shrink-0 rounded-sm border border-ig-accent bg-ig-accent-subtle px-2 py-[3px] font-sans text-[10px] font-semibold uppercase tracking-[0.1em] text-ig-accent">
            {t('nav.adminLabel', { defaultValue: 'ADMIN' })}
          </span>
        ) : null}
      </div>
    </MobileNavHeaderShell>
  );
}

function LoggedInMainPanel({
  user,
  initials,
  pack,
  packRemaining,
  packProgress,
  packTypeLabel,
  currentLangLabel,
  isAdmin,
  restricted,
  onNavigate,
  onOpenLanguage,
  onOpenSettings,
  onOpenAdmin,
  onSignOut,
  t,
}: {
  user: AuthUser;
  initials: string;
  pack: AuthPack | null;
  packRemaining: number;
  packProgress: number;
  packTypeLabel: string;
  currentLangLabel: string;
  isAdmin: boolean;
  restricted: boolean;
  onNavigate: () => void;
  onOpenLanguage: () => void;
  onOpenSettings: () => void;
  onOpenAdmin: () => void;
  onSignOut: () => void;
  t: TFunction;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div data-mobile-nav-block className="rounded-lg border border-ig-border bg-ig-bg-primary p-5">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-ig-border bg-ig-bg-tertiary text-[18px] font-medium text-ig-text-primary">
            {user.avatar ? (
              <img src={resolveAvatarSrc(user.avatar)} alt="" className="h-full w-full object-cover" />
            ) : (
              initials
            )}
          </div>
          <div className="min-w-0">
            <p className="truncate font-sans text-[18px] font-semibold text-ig-text-primary">
              {user.name?.trim() || user.email}
            </p>
            <p className="truncate font-sans text-[14px] text-ig-text-secondary">{user.email}</p>
          </div>
        </div>
      </div>

      <nav className="mt-8 flex flex-col" aria-label={t('nav.dashboard')}>
        {!restricted ? (
          <>
            <MobileNavRowLink
              to="/dashboard"
              iconName="home"
              label={t('nav.dashboard')}
              onClick={onNavigate}
            />
            {isAdmin ? (
              <MobileNavRowButton
                iconName="shield"
                label={t('admin.breadcrumbs.admin', { defaultValue: 'Admin Panel' })}
                badge={
                  <span className="rounded-sm border border-ig-accent bg-ig-accent-subtle px-2 py-[3px] font-sans text-[10px] font-semibold uppercase tracking-[0.1em] text-ig-accent">
                    {t('nav.adminLabel', { defaultValue: 'ADMIN' })}
                  </span>
                }
                onClick={onOpenAdmin}
              />
            ) : null}
            {user.isCompany ? (
              <MobileNavRowLink
                to="/company"
                iconName="briefcase-business"
                label={t('nav.companyPanel', { defaultValue: 'Company panel' })}
                onClick={onNavigate}
              />
            ) : null}
            <MobileNavRowButton
              iconName="settings"
              label={t('nav.settings')}
              badge={
                pack ? (
                  <span className="rounded-sm bg-ig-accent px-2.5 py-[3px] font-sans text-[11px] font-bold text-[var(--ig-accent-text)]">
                    {packRemaining}
                  </span>
                ) : null
              }
              onClick={onOpenSettings}
            />
          </>
        ) : null}
        <MobileNavRowButton
          iconName="globe"
          label={t('nav.language')}
          value={currentLangLabel}
          onClick={onOpenLanguage}
          showBorder={false}
        />
      </nav>

      <Button
        type="button"
        variant="secondary"
        data-mobile-nav-item
        className="h-14 justify-start gap-4 border-0 bg-transparent px-0 font-display text-[18px] font-normal text-ig-danger transition-transform hover:bg-transparent active:scale-[0.98] [&>span]:contents"
        onClick={onSignOut}
      >
        <Icon name="log-out" className="h-6 w-6 shrink-0" />
        {t('nav.signOut')}
      </Button>

      {pack && !restricted ? (
        <PackCard
          pack={pack}
          packRemaining={packRemaining}
          packProgress={packProgress}
          packTypeLabel={packTypeLabel}
          t={t}
        />
      ) : null}

      <div data-mobile-nav-block className="mt-auto flex justify-center pt-8">
        <p className="font-sans text-[11px] text-ig-text-tertiary">© {new Date().getFullYear()} InterviewGator</p>
      </div>
    </div>
  );
}

function PackCard({
  pack,
  packRemaining,
  packProgress,
  packTypeLabel,
  t,
}: {
  pack: AuthPack;
  packRemaining: number;
  packProgress: number;
  packTypeLabel: string;
  t: TFunction;
}) {
  return (
    <div data-mobile-nav-block className="mt-8 rounded-lg border border-ig-border bg-ig-bg-primary p-5">
      <div className="flex items-center justify-between">
        <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-ig-accent">{t('dashboard.yourPack', { defaultValue: 'Your Plan' })}</p>
        <span className="rounded-sm bg-ig-accent-subtle px-2 py-[3px] font-sans text-[11px] font-semibold text-ig-accent">
          {packTypeLabel}
        </span>
      </div>
      <div className="mt-3 space-y-1">
        <p className="font-sans text-[24px] font-semibold text-ig-text-primary">
          {packRemaining} / {pack.totalSessions}
        </p>
        <p className="font-sans text-[13px] text-ig-text-secondary">{t('settings.pack.sessionsRemaining')}</p>
      </div>
      <div className="mt-3 h-1 w-full rounded-full bg-ig-bg-tertiary">
        <div
          className="h-1 rounded-full bg-ig-accent"
          style={{ width: `${packProgress}%` }}
        />
      </div>
    </div>
  );
}

function GuestMainPanel({
  variant,
  landingNavLinks,
  currentLangLabel,
  onClose,
  onLandingSectionClick,
  onOpenLanguage,
  hideGuestAuthButtons,
  showGuestLanguageOnly,
  t,
}: {
  variant: MobileNavMenuProps['variant'];
  landingNavLinks: { to: string; label: string; iconName: IconName }[];
  currentLangLabel: string;
  onClose: () => void;
  onLandingSectionClick: (event: MouseEvent<HTMLAnchorElement>, path: string) => void;
  onOpenLanguage: () => void;
  hideGuestAuthButtons: boolean;
  showGuestLanguageOnly: boolean;
  t: TFunction;
}) {
  const navigate = useNavigate();

  if (showGuestLanguageOnly) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-col">
          <MobileNavRowButton
            iconName="globe"
            label={t('nav.language')}
            value={currentLangLabel}
            onClick={onOpenLanguage}
            showBorder={false}
          />
        </div>
        <MobileNavSocialFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <nav className="flex flex-col" aria-label={t('landing.nav.pageSections')}>
        {landingNavLinks.map((item) => (
          <MobileNavRowLink
            key={item.to}
            to={item.to}
            iconName={item.iconName}
            label={item.label}
            onClick={onClose}
            onLinkClick={variant === 'landing' ? onLandingSectionClick : undefined}
          />
        ))}
      </nav>

      <div className="flex flex-col">
        {hideGuestAuthButtons ? null : (
          <MobileNavRowLink to="/login" iconName="log-in" label={t('nav.login')} onClick={onClose} />
        )}
        <MobileNavRowButton
          iconName="globe"
          label={t('nav.language')}
          value={currentLangLabel}
          onClick={onOpenLanguage}
          showBorder={false}
        />
        {hideGuestAuthButtons ? null : (
          <Button
            type="button"
            data-mobile-nav-item
            onClick={() => {
              onClose();
              navigate('/register');
            }}
            className="mt-4 h-12 w-full rounded-lg px-6 text-[14px] font-medium transition-transform active:scale-[0.98]"
          >
            {t('landing.hero.getStarted')}
          </Button>
        )}
      </div>

      <MobileNavSocialFooter />
    </div>
  );
}

function LanguagePanel({
  currentLang,
  languageLabels,
  showFooter,
  onPick,
}: {
  currentLang: SupportedLanguage;
  languageLabels: Record<SupportedLanguage, string>;
  showFooter: boolean;
  onPick: (code: SupportedLanguage) => void;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-col">
        {SUPPORTED_LANGUAGES.map((code) => {
          const selected = code === currentLang;
          return (
            <Button
              key={code}
              type="button"
              variant="secondary"
              data-mobile-nav-item
              className="h-14 w-full justify-start gap-4 rounded-none border-0 border-b border-ig-border bg-transparent px-0 py-4 transition-transform hover:bg-transparent active:scale-[0.98] [&>span]:contents"
              onClick={() => onPick(code)}
            >
              <span className="font-display text-[18px] font-normal text-ig-text-primary">
                {languageLabels[code]}
              </span>
              <span className="flex-1" />
              {selected ? (
                <Icon
                  name="check"
                  data-mobile-nav-check
                  className="h-5 w-5 text-ig-accent"
                  strokeWidth={2.5}
                />
              ) : null}
            </Button>
          );
        })}
      </div>
      {showFooter ? (
        <div className="mt-auto pt-6">
          <MobileNavSocialFooter />
        </div>
      ) : (
        <div className="flex-1" />
      )}
    </div>
  );
}

function SettingsPanel({
  pathname,
  packRemaining,
  onNavigate,
  t,
}: {
  pathname: string;
  packRemaining: number;
  onNavigate: () => void;
  t: TFunction;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <nav className="flex flex-col" aria-label={t('nav.settings')}>
        {settingsNavItems.map((item) => {
          const active = isSettingsNavActive(pathname, item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              data-mobile-nav-item
              className={cn(
                'flex h-14 items-center gap-4 border-b border-ig-border py-4 transition-transform active:scale-[0.98]',
                active && 'bg-ig-accent-subtle',
              )}
              onClick={onNavigate}
            >
              <Icon
                name={item.iconName}
                className={cn('h-6 w-6 shrink-0', active ? 'text-ig-accent' : 'text-ig-text-secondary')}
              />
              <span
                className={cn(
                  'font-display text-[18px] font-normal',
                  active ? 'text-ig-accent' : 'text-ig-text-primary',
                )}
              >
                {t(item.labelKey, { defaultValue: item.defaultLabel })}
              </span>
              <span className="flex-1" />
              {item.to === '/settings/plan' && packRemaining > 0 ? (
                <span className="rounded-sm bg-ig-accent px-2.5 py-[3px] font-sans text-[11px] font-bold text-[var(--ig-accent-text)]">
                  {packRemaining}
                </span>
              ) : null}
              <Icon name="chevron-right" className="h-[18px] w-[18px] shrink-0 text-ig-text-tertiary" />
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

function AdminPanel({
  pathname,
  onNavigate,
  onBackToApp,
  t,
}: {
  pathname: string;
  onNavigate: () => void;
  onBackToApp: () => void;
  t: TFunction;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <nav className="flex flex-col" aria-label={t('admin.sidebar.admin', { defaultValue: 'ADMIN' })}>
        {adminNavItems.map((item) => {
          const active = isAdminNavItemActive(pathname, item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              data-mobile-nav-item
              className={cn(
                'flex h-14 items-center gap-4 border-b border-ig-border py-4 transition-transform active:scale-[0.98]',
                active && 'bg-ig-accent-subtle',
              )}
              onClick={onNavigate}
            >
              <Icon
                name={item.iconName}
                className={cn('h-6 w-6 shrink-0', active ? 'text-ig-accent' : 'text-ig-text-secondary')}
              />
              <span
                className={cn(
                  'font-display text-[18px] font-normal',
                  active ? 'text-ig-accent' : 'text-ig-text-primary',
                )}
              >
                {t(item.labelKey, { defaultValue: item.defaultLabel })}
              </span>
              <span className="flex-1" />
              <Icon name="chevron-right" className="h-[18px] w-[18px] shrink-0 text-ig-text-tertiary" />
            </Link>
          );
        })}
      </nav>

      <Link
        to="/dashboard"
        data-mobile-nav-item
        className="mt-6 flex h-14 items-center gap-4 pt-6 font-sans text-[16px] text-ig-text-secondary transition-transform active:scale-[0.98]"
        onClick={onBackToApp}
      >
        <Icon name="arrow-left" className="h-6 w-6 shrink-0 text-ig-text-secondary" />
        {t('admin.sidebar.backToApp', { defaultValue: 'Back to App' })}
      </Link>
    </div>
  );
}

function MobileNavSocialFooter() {
  return (
    <div data-mobile-nav-block className="mt-auto flex flex-col items-center gap-3 pt-6">
      <div className="flex items-center gap-4">
        <Icon name="linkedin" className="h-5 w-5 text-ig-accent" aria-hidden />
        <Icon name="facebook" className="h-5 w-5 text-ig-accent" aria-hidden />
        <Icon name="message-circle" className="h-5 w-5 text-ig-accent" aria-hidden />
      </div>
      <p className="text-center font-sans text-[11px] font-normal text-ig-text-tertiary">
        © {new Date().getFullYear()} InterviewGator
      </p>
    </div>
  );
}

function MobileNavRowLink({
  to,
  iconName,
  label,
  value,
  badge,
  onClick,
  onLinkClick,
  showBorder = true,
}: {
  to: string;
  iconName: IconName;
  label: string;
  value?: string;
  badge?: ReactNode;
  onClick: () => void;
  onLinkClick?: (event: MouseEvent<HTMLAnchorElement>, to: string) => void;
  showBorder?: boolean;
}) {
  return (
    <Link
      to={to}
      data-mobile-nav-item
      className={cn(
        'flex h-14 items-center gap-4 py-4 transition-transform active:scale-[0.98]',
        showBorder && 'border-b border-ig-border',
      )}
      onClick={(event) => {
        onLinkClick?.(event, to);
        if (!event.defaultPrevented) {
          onClick();
        }
      }}
    >
      <MobileNavRowContent iconName={iconName} label={label} value={value} badge={badge} />
    </Link>
  );
}

function MobileNavRowButton({
  iconName,
  label,
  value,
  badge,
  onClick,
  showBorder = true,
}: {
  iconName: IconName;
  label: string;
  value?: string;
  badge?: ReactNode;
  onClick: () => void;
  showBorder?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="secondary"
      data-mobile-nav-item
      className={cn(
        'h-14 w-full justify-start gap-4 border-0 bg-transparent px-0 py-4 text-left transition-transform hover:bg-transparent active:scale-[0.98] [&>span]:contents',
        showBorder && 'border-b border-ig-border',
      )}
      onClick={onClick}
    >
      <MobileNavRowContent iconName={iconName} label={label} value={value} badge={badge} />
    </Button>
  );
}

function MobileNavRowContent({
  iconName,
  label,
  value,
  badge,
}: {
  iconName: IconName;
  label: string;
  value?: string;
  badge?: ReactNode;
}) {
  return (
    <>
      <Icon name={iconName} className="h-6 w-6 shrink-0 text-ig-text-secondary" />
      <span className="font-display text-[18px] font-normal text-ig-text-primary">{label}</span>
      <span className="flex-1" />
      {value ? <span className="font-sans text-[16px] text-ig-text-secondary">{value}</span> : null}
      {badge ?? null}
      <Icon name="chevron-right" className="h-[18px] w-[18px] shrink-0 text-ig-text-tertiary" />
    </>
  );
}
